import { AltLabel } from '../../TreeViewTypes';
import { DataNode, LabelContext } from '../../data_nodes/types';
import { LabelGenerator, DefaultLabelGenerator } from '../types';
import { filterItemsForInterval } from '../../../utils/statusUtils';
import { analyzeObjectiveStatus } from '../../../utils/statusSummaryUtils';
import { getVariantFromStatusAnalysis, createVariantMetadata } from '../../utils/iconVariants';

/**
 * Generate default label for Status Report Date nodes
 *
 * Uses the comprehensive alt labels logic to ensure consistency.
 */
export const generateStatusReportDateDefaultLabel: DefaultLabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): string => {
  const altLabels = generateStatusReportDateAltLabels(dataNode, context);
  const defaultLabel = altLabels.find((label) => label.key === 'default');
  return defaultLabel?.value || 'No status report date';
};

/**
 * Generate alt labels for Status Report Date nodes
 *
 * Provides comprehensive summary labels for accessibility including inchstone/milestone
 * detection and status information that would otherwise only be available visually.
 *
 * * Context-aware generator that adapts behavior based on tree structure:
 * - Dates tree: StatusReportDate is ancestor of Objectives → show summary statistics
 * - Objectives tree: StatusReportDate is descendant of Objective → show date inf
 */
export const generateStatusReportDateAltLabels: LabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): AltLabel[] => {
  // Context detection for tree-aware behavior
  const isStatusReportDateAncestorOfObjective = context.allChildren.some(
    (child) => child.type === 'Objective',
  );
  const isStatusReportDateDescendantOfObjective = context.ancestors.some(
    (ancestor) => ancestor.type === 'Objective',
  );

  if (isStatusReportDateAncestorOfObjective) {
    // Dates tree: StatusReportDate has Objective descendants → show summary statistics
    return generateDatesTreeLabels(dataNode, context);
  } else if (isStatusReportDateDescendantOfObjective) {
    // Objectives tree: StatusReportDate has Objective ancestors → show date info
    return generateObjectivesTreeLabels(dataNode, context);
  } else {
    // Unexpected case - log error and fallback
    console.warn('Unexpected tree structure in StatusReportDate generator:', {
      nodeType: dataNode.type,
      hasObjectiveDescendants: isStatusReportDateAncestorOfObjective,
      hasObjectiveAncestors: isStatusReportDateDescendantOfObjective,
      ancestors: context.ancestors.map((a) => a.type),
      children: context.allChildren.map((c) => c.type),
      context,
    });
    return generateFallbackLabels(dataNode);
  }
};

/**
 * Generate labels for StatusReportDate in Dates tree (summary statistics)
 */
function generateDatesTreeLabels(dataNode: DataNode<any>, context: LabelContext): AltLabel[] {
  const data = dataNode.data;

  // Extract date
  let date: Date | null = null;
  if (data && data.interval && data.interval.end) {
    date = data.interval.end;
  } else if (data && data.statusDate) {
    date = new Date(data.statusDate);
  }

  const dateString = date ? date.toLocaleDateString() : 'Unknown Date';

  // TODO: Calculate summary statistics from descendant objectives
  // For now, use a placeholder variant and show basic date info
  const variant = 'status-none'; // Placeholder until summary statistics are implemented
  dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };

  return [
    {
      key: 'default',
      selectOption: 'Default: Date with summary statistics',
      value: `${dateString} (summary statistics not yet implemented)`,
    },
    {
      key: 'date-only',
      selectOption: 'Date-only',
      value: dateString,
    },
  ];
}

/**
 * Generate labels for StatusReportDate in Objectives tree (existing logic)
 */
function generateObjectivesTreeLabels(dataNode: DataNode<any>, context: LabelContext): AltLabel[] {
  const data = dataNode.data;

  // Extract date
  let date: Date | null = null;
  if (data && data.interval && data.interval.end) {
    date = data.interval.end;
  } else if (data && data.statusDate) {
    date = new Date(data.statusDate);
  }

  const dateString = date ? date.toLocaleDateString() : 'Unknown Date';

  // Get objective from parent context (StatusReportDate is child of Objective in objectives tree)
  const parent = context.ancestors[context.ancestors.length - 1];
  const objective = parent?.data;

  let inchstoneOrMilestone = '';
  let shortInchstoneOrMilestone = '';

  // Determine if this is an inchstone or milestone by checking for results in this interval
  if (objective && data && data.interval) {
    const resultsForInterval = filterItemsForInterval(
      objective.results || [],
      data.interval.start,
      data.interval.end,
    );

    const hasResults = resultsForInterval.length > 0;
    const hasKeyResults = resultsForInterval.some((result: any) => result.isKeyResult);

    if (hasKeyResults) {
      inchstoneOrMilestone = 'Milestone';
      shortInchstoneOrMilestone = 'M';
    } else if (hasResults) {
      inchstoneOrMilestone = 'Inchstone';
      shortInchstoneOrMilestone = 'I';
    }
  }

  // Determine status color and code
  const status = data?.status;
  let statusColor = '';
  let statusCode = '';

  if (status && status.statusCode) {
    statusCode = status.statusCode;

    // Map status codes to color names
    const colorMap: { [key: string]: string } = {
      g: 'green',
      y: 'yellow',
      r: 'red',
      p: 'planned',
      '?': 'TBD',
    };
    statusColor = colorMap[statusCode] || statusCode;

    // ✅ Set icon variant based on status for StatusReportDate
    const statusAnalysis = {
      type: statusCode === 'p' || statusCode === '?' ? ('planning' as const) : ('actual' as const),
      statusCode: status.statusCode,
      date: null,
      displayName: statusColor,
    };
    const variant = getVariantFromStatusAnalysis(statusAnalysis);
    if (variant) {
      dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
    }
  } else if (inchstoneOrMilestone) {
    // Future dates with results/key results but no status - call it pending
    statusColor = 'pending';
    statusCode = 'pending';

    // ✅ Set icon variant for pending status
    const variant = 'status-none';
    dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
  }

  const labels: AltLabel[] = [];

  // Default: Date (Status Color) - Inchstone/Milestone
  let defaultValue = dateString;
  if (inchstoneOrMilestone && statusColor) {
    defaultValue = `${dateString} (${statusColor}) - ${inchstoneOrMilestone}`;
  } else if (inchstoneOrMilestone) {
    defaultValue = `${dateString} - ${inchstoneOrMilestone}`;
  } else if (statusColor) {
    defaultValue = `${dateString} (${statusColor})`;
  }

  labels.push({
    key: 'default',
    selectOption: 'Default: Date (Status Color) - Inchstone/Milestone',
    value: defaultValue,
  });

  // Short: I/M, Status Code
  if (inchstoneOrMilestone && statusCode && statusCode !== 'pending') {
    labels.push({
      key: 'short',
      selectOption: 'Short: Date (Status Code) - I/M',
      value: `${dateString} (${statusCode}) - ${shortInchstoneOrMilestone}`,
    });
  }

  // Date-only
  labels.push({
    key: 'date-only',
    selectOption: 'Date-only',
    value: dateString,
  });

  // TODO: If a deadline has passed, the result has not been delivered, and the status is not a color,
  // we should auto-update this to red. We'll leave this for another day though.

  return labels;
}

/**
 * Generate fallback labels when context detection fails
 */
function generateFallbackLabels(dataNode: DataNode<any>): AltLabel[] {
  const data = dataNode.data;
  // Extract date
  let date: Date | null = null;
  if (data && data.interval && data.interval.end) {
    date = data.interval.end;
  } else if (data && data.statusDate) {
    date = new Date(data.statusDate);
  }

  return [
    {
      key: 'default',
      // If there are no objectives on the map yet, this will get triggered.
      selectOption: 'Error: Missing data',
      value: date ? date.toLocaleDateString() : 'Unknown Date',
    },
  ];
}
